import type { FaceDetection as MediapipeFaceDetection, Results } from '@mediapipe/face_detection';

export type MediapipeDetectionBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

let detector: MediapipeFaceDetection | null = null;
let detectorInitPromise: Promise<MediapipeFaceDetection> | null = null;
let detectionQueue: Promise<MediapipeDetectionBox | null> = Promise.resolve(null);

const locateFile = (file: string) => {
  // Verbesserte locateFile-Funktion für Produktionsserver
  const baseUrl = import.meta.env.BASE_URL || '/';
  
  // Entferne führende Slashes für konsistente Pfade
  const cleanBaseUrl = baseUrl.replace(/^\/+|\/+$/g, '');
  const cleanFile = file.replace(/^\/+/, '');
  
  // Verschiedene mögliche Pfade testen
  const possiblePaths = [
    (cleanBaseUrl ? '/' + cleanBaseUrl : '') + '/mediapipe/face_detection/' + cleanFile,
    (cleanBaseUrl ? '/' + cleanBaseUrl : '') + '/dist/mediapipe/face_detection/' + cleanFile,
    (cleanBaseUrl ? '/' + cleanBaseUrl : '') + '/public/mediapipe/face_detection/' + cleanFile,
    '/mediapipe/face_detection/' + cleanFile,
    './mediapipe/face_detection/' + cleanFile
  ];
  
  const selectedPath = possiblePaths[0];
  
  console.log('[Mediapipe] locateFile: ' + file + ' -> ' + selectedPath);
  
  return selectedPath;
};

async function ensureDetector(): Promise<MediapipeFaceDetection> {
  if (detector) {
    return detector;
  }

  if (!detectorInitPromise) {
    detectorInitPromise = (async () => {
      try {
        console.log('[Mediapipe] Initialisiere FaceDetection...');
        
        const mod: any = await import('@mediapipe/face_detection');
        try {
          console.log('[Mediapipe] Module exports keys:', Object.keys(mod));
          console.log('[Mediapipe] typeof window.FaceDetection:', typeof (window as any).FaceDetection);
        } catch {}
        const FaceDetectionCtor: any =
          (window as any)?.FaceDetection ?? mod?.FaceDetection ?? mod?.default?.FaceDetection ?? undefined;

        if (!FaceDetectionCtor) {
          throw new Error('FaceDetection constructor not found in module exports');
        }

        console.log('[Mediapipe] FaceDetection importiert, erstelle Instanz...');

        const instance = new FaceDetectionCtor({ locateFile });
        
        console.log('[Mediapipe] Setze Optionen...');
        instance.setOptions({
          model: 'short',
          selfieMode: false,
          minDetectionConfidence: 0.5,
        });
        
        console.log('[Mediapipe] Initialisiere...');
        await instance.initialize();
        
        console.log('[Mediapipe] ✅ Erfolgreich initialisiert');
        detector = instance;
        return instance;
        
      } catch (error) {
        console.error('[Mediapipe] ❌ Initialisierung fehlgeschlagen:', error);
        detectorInitPromise = null;
        throw error;
      }
    })();
  }

  return detectorInitPromise;
}

function convertResultsToBox(results: Results, image: HTMLImageElement | HTMLCanvasElement): MediapipeDetectionBox | null {
  if (!results.detections || results.detections.length === 0) {
    return null;
  }

  const [firstDetection] = results.detections;
  if (!firstDetection?.boundingBox) {
    return null;
  }

  const { xCenter, yCenter, height, width } = firstDetection.boundingBox;
  const imgWidth = 'width' in image ? image.width : (image as HTMLCanvasElement).width;
  const imgHeight = 'height' in image ? image.height : (image as HTMLCanvasElement).height;

  const absoluteWidth = width * imgWidth;
  const absoluteHeight = height * imgHeight;
  const absoluteX = xCenter * imgWidth - absoluteWidth / 2;
  const absoluteY = yCenter * imgHeight - absoluteHeight / 2;

  return {
    x: Math.max(0, absoluteX),
    y: Math.max(0, absoluteY),
    width: Math.max(0, absoluteWidth),
    height: Math.max(0, absoluteHeight),
  };
}

async function runDetection(image: HTMLImageElement | HTMLCanvasElement): Promise<MediapipeDetectionBox | null> {
  try {
    console.log('[Mediapipe] Starte Gesichtserkennung...');
    
    const instance = await ensureDetector();
    
    return await new Promise<MediapipeDetectionBox | null>((resolve) => {
      let resolved = false;
      const cleanup = () => {
        instance.onResults(() => {});
      };

      instance.onResults((results) => {
        if (resolved) return;
        resolved = true;
        cleanup();
        
        console.log('[Mediapipe] Ergebnisse erhalten:', results);
        
        const box = convertResultsToBox(results, image);
        if (box) {
          console.log('[Mediapipe] ✅ Gesicht gefunden:', box);
        } else {
          console.log('[Mediapipe] ❌ Kein Gesicht gefunden');
        }
        
        resolve(box);
      });

      console.log('[Mediapipe] Sende Bild...');
      instance
        .send({ image })
        .then(() => {
          if (!resolved) {
            resolved = true;
            cleanup();
            console.log('[Mediapipe] Send abgeschlossen, aber keine Ergebnisse');
            resolve(null);
          }
        })
        .catch((error) => {
          console.error('[Mediapipe] Send fehlgeschlagen:', error);
          if (!resolved) {
            resolved = true;
            cleanup();
            resolve(null);
          }
        });
    });
  } catch (error) {
    console.error('[Mediapipe] ❌ Gesichtserkennung fehlgeschlagen:', error);
    return null;
  }
}

export function detectFaceWithMediapipe(image: HTMLImageElement | HTMLCanvasElement): Promise<MediapipeDetectionBox | null> {
  // Mediapipe soll auf Produktionsserver funktionieren!
  console.log('[Mediapipe] Versuche Mediapipe FaceDetection auf Produktionsserver...');
  
  detectionQueue = detectionQueue.then(() => runDetection(image));
  return detectionQueue;
}

export function resetMediapipeDetector() {
  detector?.reset?.();
}

// Debug-Funktion
export function debugMediapipe() {
  console.log('[Mediapipe] Debug:');
  console.log('Detector:', detector ? 'initialisiert' : 'nicht initialisiert');
  console.log('Init Promise:', detectorInitPromise ? 'läuft' : 'nicht aktiv');
  console.log('Detection Queue:', detectionQueue !== null ? 'aktiv' : 'nicht aktiv');
}